import React, { useEffect, useState } from 'react';
import Modal from '../../components/Shell/Modal';
import useModal from '../../hooks/useModal';
import {
	ArrowPathIcon,
	CheckCircleIcon,
	ClockIcon,
	DocumentCheckIcon,
	MinusCircleIcon,
	PlusCircleIcon,
	PlusIcon,
	WrenchScrewdriverIcon,
	XCircleIcon,
	XMarkIcon,
} from '@heroicons/react/24/outline';
import {
	AttributeRow,
	TemplateBoolean,
	TemplateCheckbox,
	TemplateContact,
	TemplateCreateable,
	TemplateDate,
	TemplateDropdown,
	TemplateField,
	TemplateFile,
	TemplateReactSelect,
	TemplateTeamMembers,
} from '../../components/Attributes/Attributes_old';
import usePrevious from '../../hooks/usePrevious';
import useAsync from '../../hooks/useAsync';
import {
	getModuleCategories,
	getModuleFieldsBeta,
	updateModuleFieldActive,
} from '../../api/modules';
import Button from '../../components/Button';
import Attributes, { configurePayload } from '../../components/Attributes';
import LoadingSkeleton from '../../components/Shell/LoadingSkeleton';
import { FIELD_OPTIONS } from '../../components/helpers/constants';
import {
	capitalizeFirstLetter,
	classNames,
	getUserFullName,
} from '../../components/helpers/functions';
import { useRequiredBeta } from '../../hooks/useRequired';
import { addContract } from '../../api';
import { getDocuments } from '../../api/documents';
import moment from 'moment';
import { NOTIFY_OPTIONS } from '../../constants';
import { AvatarGenerated } from '../../components/Avatar';
import { getTeam } from '../../api/company';
import { addService } from '../../api/services';
import GeneralStepTask from './steps/GeneralStepTask';
import ScheduleStepTask from './steps/ScheduleStepTask';
import AttributesStepAsset from '../Assets/steps/AttributesStepAsset';
import AttributeStepTask from './steps/AttributeStepTask';

const MODAL_NAME = 'taskAdd';
const DEFAULT_FORM = {};
const SERVICE_MODULE_ID = process.env.REACT_APP_MODULE_ID_SERVICE;
const EVENT_MODULE_ID = process.env.REACT_APP_MODULE_ID_EVENT;

const DEFAULT_NOTIFICATION_FORM = { time: 30, admin: true };

const AddTaskModal = () => {
	const [currentTaskStep, setCurrentTaskStep] = useState(1);
	const [currentStep, setCurrentStep] = useState(0);

	const [form, setForm] = useState({});
	const [updatedForm, setUpdateForm] = useState({ taskSchedule: [] });
	const [taskForm, setTaskForm] = useState({});
	const [notificationForm, setNotificationForm] = useState(
		DEFAULT_NOTIFICATION_FORM
	);

	const { execute: onAddService } = useAsync(addService, { immediate: false });

	const { open: manageAttributesSettings, setOpen: openManageAttributes } =
		useModal('manageAttributes');

	const [isAddTaskModalOpen, setAddTaskModal] = useState(false);
	const [isAddCustomAttributesModalOpen, setAddCustomAttributesModal] =
		useState(false);

	const { open: options, setClose } = useModal(MODAL_NAME);

	const { open: addCategoryOpen, setOpen: setAddCategoryOpen } =
		useModal('addCategoryModal');

	const {
		value: moduleFields,
		status: isLoadingModuleFields,
		execute: onRefreshModuleFields,
	} = useAsync(getModuleFieldsBeta, {
		defaultPayload: { id: SERVICE_MODULE_ID, active: true },
	});

	const { execute: onSetFieldActive } = useAsync(updateModuleFieldActive, {
		immediate: false,
	});

	const { value: taskModuleFields, status: isLoadingTaskModuleFields } =
		useAsync(getModuleFieldsBeta, {
			defaultPayload: { id: SERVICE_MODULE_ID, active: true },
		});

	const {
		value: categories,
		execute: onRefreshCategories,
		status: isLoadingCategories,
	} = useAsync(getModuleCategories, {
		immediate: false,
	});
	const {
		value: documents,
		execute: onRefreshDocuments,
		status: isLoadingDocuments,
	} = useAsync(getDocuments, {
		immediate: false,
	});

	const {
		value: eventCategories,
		execute: onRefreshEventCategories,
		status: isLoadingEventCategories,
	} = useAsync(getModuleCategories, { immediate: false });

	const { execute: handleAddContract, status: addingContract } = useAsync(
		addContract,
		{ immediate: false }
	);

	const { value: teamMembers } = useAsync(getTeam);

	useEffect(() => {
		if (options?.contract && moduleFields?.combined?.length) {
			const SERVICE_START_FIELD_ID = moduleFields?.combined?.find(
				(o) => o.key === 'serviceStart' && o.isDefault
			)?.id;

			const ASSETS_FIELD_ID = moduleFields?.combined?.find(
				(o) => o.key === 'assets' && o.isDefault
			)?.id;

			setUpdateForm({
				...updatedForm,
				serviceStart: {
					fieldId: SERVICE_START_FIELD_ID,
					value: options?.contract?.startDate,
				},
				assets: {
					fieldId: ASSETS_FIELD_ID,
					value: options?.contract?.assets?.map((asset) => ({
						label: '',
						value: asset?.id,

						asset,
					})),
				},
			});
		}
	}, [options, moduleFields]);

	useEffect(() => {
		if (options?.entityType === 'asset') {
			const ASSETS_FIELD_ID = moduleFields?.combined?.find(
				(o) => o.key === 'assets' && o.isDefault
			)?.id;

			setUpdateForm({
				...updatedForm,
				assets: {
					fieldId: ASSETS_FIELD_ID,
					value: [{ label: '', value: options?.entityId }],
				},
			});
		}
	}, [options?.entityId]);

	const prevManageAttributes = usePrevious(manageAttributesSettings);
	useEffect(() => {
		if (!manageAttributesSettings && prevManageAttributes) {
			onRefreshModuleFields(null, null, { silent: true });
		}
	}, [manageAttributesSettings]);

	const previousCategory = usePrevious(form?.category?.value?.value);
	useEffect(() => {
		if (
			form?.category?.value?.value &&
			previousCategory !== form?.category?.value?.value
		) {
			onRefreshModuleFields(null, null, { silent: true });
		}
	}, [form?.category?.value?.value]);

	const prevIsAddTaskModalOpen = usePrevious(isAddTaskModalOpen);
	const prevTaskModuleFields = usePrevious(taskModuleFields);
	useEffect(() => {
		if (currentStep === 2) {
			onRefreshEventCategories({
				id: EVENT_MODULE_ID,
			});
		}
	}, [currentStep]);

	useEffect(() => {
		if (isAddTaskModalOpen && taskModuleFields) {
			let obj = {};
			taskModuleFields?.standard
				?.filter((field) => field?.defaultValue)
				?.map((field) => {
					obj[field?.key] = { fieldId: field?.id, value: field?.defaultValue };
				});

			setTaskForm({ ...taskForm, ...obj });
		}
	}, [taskModuleFields, isAddTaskModalOpen]);

	const previousCoverageStart = usePrevious(
		updatedForm['coverage-start']?.value
	);
	useEffect(() => {
		if (previousCoverageStart !== updatedForm['coverage-start']) {
			setUpdateForm({
				...updatedForm,
				'coverage-end': {
					...(updatedForm['coverage-end'] || {
						fieldId: moduleFields?.combined?.find(
							(o) => o.key === 'coverage-end' && o.isDefault
						)?.id,
					}),
					value: moment(updatedForm['coverage-start']?.value)
						.add(1, 'year')
						.subtract(1, 'day')
						.toDate(),
				},
			});
		}
	}, [updatedForm['coverage-start']?.value]);

	const SERVICE_CONTRACT_CATEGORY = categories?.find(
		(category) =>
			category?.key === 'default:servicecontract' && category.isDefault
	);

	const EVENT_STATUS_FIELD_ID = moduleFields?.combined?.find(
		(o) => o.key === 'defaultEventStatus' && o.isDefault
	)?.id;

	const id = null;
	const skipModuleKeys = [
		...(id ? ['coverage-start', 'duration'] : ['category']),
		...(options?.assetId ? ['assets'] : []),
		'defaultEventStatus',
	];
	const skipTaskModuleKeys = [
		'name',
		'description',
		'assets',
		'defaultEventStatus',
	];

	const hasAllRequired = useRequiredBeta(
		{ ...form, ...updatedForm },
		moduleFields?.combined
			?.filter((field) => field.isRequired)
			.map((field) => field.key)
	);

	const onAddTask = () => {
		const taskObject = {
			...configurePayload(
				{
					...updatedForm,
					...form,
				},
				moduleFields?.combined,
				null
				//updatedForm?.teamMember
			),
		};

		onAddService(
			{
				...(options?.contract?.id
					? {
							contract: { id: options?.contract?.id },
					  }
					: {}),
				...taskObject,
				...(updatedForm?.occurrences?.value === 'custom'
					? { dates: updatedForm?.occurrences?.dates }
					: {}),
			},
			(response) => {
				setClose();
				setCurrentStep(0);
				setForm({});
				setUpdateForm({});
			},
			{
				showNotification: {
					success: 'Task created',
					error: 'Something went wrong...',
				},
			}
		);
	};

	const steps = [
		{ name: 'General' },
		{ name: 'Attributes' },
		{ name: 'Schedule' },
		...(options?.entityType === 'asset' && options?.entityId
			? []
			: [{ name: 'Assets' }]),
	];

	const moduleFieldsBreakdown = {
		general: [
			...(moduleFields?.standard?.filter(
				(field) =>
					field?.isDefault &&
					['name', 'description', 'teamMember', 'defaultEventType'].includes(
						field?.key
					)
			) || []),
		],
		event: moduleFields?.standard?.filter(
			(field) =>
				field?.isDefault &&
				['serviceStart', 'occurrences', 'defaultEventStatus'].includes(
					field?.key
				)
		),
		assets: moduleFields?.standard?.filter(
			(field) => field?.isDefault && ['assets'].includes(field?.key)
		),
		customAttributes:
			moduleFields?.combined?.filter((field) => !field?.isDefault) || [],
	};

	const OCCURRENCES_OPTIONS = taskModuleFields?.standard?.find(
		(field) => field?.isDefault && field?.key === 'occurrences'
	)?.options?.options;

	return (
		<>
			<Modal
				title={currentTaskStep === 1 ? 'Create a new task' : ''}
				open={!!options}
				onClose={() => {
					if (typeof options?.onClose === 'function') {
						options?.onClose();
					}
					setClose(null);
					setCurrentStep(0);
					setCurrentTaskStep(1);
					setUpdateForm({});
				}}
				size={currentTaskStep === 1 ? '4xl' : undefined}
				preventBackdropClose
			>
				{currentTaskStep === 0 && (
					<div className="grid grid-cols-2 gap-x-4 gap-y-4">
						<div
							onClick={() => {
								/*setForm({
									...form,
									category: {
										fieldId: CATEGORY_FIELD_ID,
										value: {
											label: SERVICE_CONTRACT_CATEGORY?.name,
											value: SERVICE_CONTRACT_CATEGORY?.id,
										},
									},
								});
								setForm){
									...form,

								}*/

								setUpdateForm({
									...updatedForm,
									defaultEventType: {
										value: 'estimated',
										fieldId: EVENT_STATUS_FIELD_ID,
									},
								});
								setCurrentTaskStep(currentTaskStep + 1);
							}}
							className="border border-gray-200 hover:border-gray-300 cursor-pointer rounded-lg text-xs h-36 flex justify-center items-center font-light text-gray-600 hover:text-gray-900 flex-col"
						>
							<ClockIcon className="w-6 h-6 text-gray-600 hover:text-gray-900 mb-3" />
							Service Cycle
						</div>
						<div
							onClick={() => {
								/*setForm({
									...form,
									category: {
										fieldId: CATEGORY_FIELD_ID,
										value: {
											label: WARRANTY_CONTRACT_CATEGORY?.name,
											value: WARRANTY_CONTRACT_CATEGORY?.id,
										},
									},
								});*/
								setCurrentTaskStep(currentTaskStep + 1);
							}}
							className="border border-gray-200 hover:border-gray-300 cursor-pointer rounded-lg text-xs h-36 flex justify-center items-center font-light text-gray-600 hover:text-gray-900 flex-col"
						>
							<ArrowPathIcon className="w-6 h-6 text-gray-600 hover:text-gray-900 mb-3" />
							Recurring
						</div>
						<div className="col-span-2">
							<div className="flex justify-end mt-4">
								<Button
									type="secondary"
									onClick={() => setCurrentTaskStep(currentTaskStep + 1)}
								>
									Use Blank Template
								</Button>
							</div>
						</div>
					</div>
				)}

				{currentTaskStep === 1 && (
					<div className="grid grid-cols-4 gap-x-4">
						<div className="col-span-1 -ml-10 my-5">
							<nav aria-label="Progress" className="flex px-10">
								<ol role="list" className="space-y-6">
									{steps.map((step, stepIdx) => (
										<li key={step.name}>
											{stepIdx < currentStep ? (
												<a
													href={step.href}
													className="group cursor-pointer"
													onClick={() => setCurrentStep(stepIdx)}
												>
													<div className="flex items-center">
														<div
															aria-hidden="true"
															className="relative flex h-5 w-5 flex-shrink-0 items-center justify-center"
														>
															<div className="h-2 w-2 rounded-full bg-gray-300 group-hover:bg-gray-400" />
														</div>
														<p className="ml-3 text-xs font-medium text-gray-500 group-hover:text-gray-900">
															{step.name}
														</p>
													</div>
												</a>
											) : stepIdx === currentStep ? (
												<a
													href={step.href}
													aria-current="step"
													className="flex items-center"
												>
													<span
														aria-hidden="true"
														className="relative flex h-5 w-5 flex-shrink-0 items-center justify-center"
													>
														<span className="absolute h-4 w-4 rounded-full bg-indigo-200" />
														<span className="relative block h-2 w-2 rounded-full bg-indigo-600" />
													</span>
													<span className="ml-3 text-xs font-medium text-indigo-600">
														{step.name}
													</span>
												</a>
											) : (
												<a
													href={step.href}
													className="group cursor-pointer"
													onClick={() => setCurrentStep(stepIdx)}
												>
													<div className="flex items-center">
														<div
															aria-hidden="true"
															className="relative flex h-5 w-5 flex-shrink-0 items-center justify-center"
														>
															<div className="h-2 w-2 rounded-full bg-gray-300 group-hover:bg-gray-400" />
														</div>
														<p className="ml-3 text-xs font-medium text-gray-500 group-hover:text-gray-900">
															{step.name}
														</p>
													</div>
												</a>
											)}
										</li>
									))}
								</ol>
							</nav>
						</div>
						<div className="col-span-3">
							<div className="grid gap-y-4 pt-3">
								<>
									{isLoadingModuleFields === 'pending' ? (
										<LoadingSkeleton />
									) : (
										<>
											<div className="text-lg">{steps[currentStep]?.name}</div>
											<div className=" min-h-72">
												<div
													className={classNames(
														'grid gap-y-4 gap-x-4 text-left',
														currentStep === 4 || currentStep === 3
															? 'grid-cols-1'
															: 'grid-cols-2'
													)}
												>
													{currentStep === 0 && (
														<GeneralStepTask
															moduleFieldsBreakdown={moduleFieldsBreakdown}
															skipModuleKeys={skipModuleKeys}
															form={form}
															updatedForm={updatedForm}
															setUpdateForm={setUpdateForm}
														/>
													)}

													{currentStep === 1 && (
														<AttributeStepTask
															moduleFieldsBreakdown={moduleFieldsBreakdown}
															skipModuleKeys={skipModuleKeys}
															form={form}
															updatedForm={updatedForm}
															setUpdateForm={setUpdateForm}
														/>
													)}

													{currentStep === 2 && (
														<ScheduleStepTask
															moduleFieldsBreakdown={moduleFieldsBreakdown}
															skipModuleKeys={skipModuleKeys}
															form={form}
															updatedForm={updatedForm}
															setUpdateForm={setUpdateForm}
														/>
													)}

													{currentStep === 3 &&
														moduleFieldsBreakdown?.assets
															?.filter(
																(field) =>
																	skipModuleKeys.indexOf(field.key) === -1
															)
															?.map((field) => (
																<>
																	<Attributes
																		field={field}
																		onChange={(index, value, fullValue) => {
																			if (value || fullValue) {
																				let updatedValue = form[index]?.value;

																				if (fullValue) {
																					updatedValue = fullValue;
																				} else {
																					if (
																						updatedValue &&
																						Array.isArray(value)
																					) {
																						updatedValue = [
																							...(Array.isArray(updatedValue)
																								? updatedValue
																								: [updatedValue]),
																							...value,
																						];
																					} else {
																						updatedValue = value;
																					}
																				}

																				setUpdateForm({
																					...updatedForm,
																					[index]: {
																						value: updatedValue,
																						fieldId: field.id,
																					},
																				});
																			} else {
																				let updated = { ...updatedForm };
																				updated[index] = {
																					...updated[index],
																					value: null,
																				};

																				setUpdateForm(updated);
																			}
																		}}
																		form={{ ...form, ...updatedForm }}
																		updatedForm={updatedForm}
																		setForm={setUpdateForm}
																		moduleId={SERVICE_MODULE_ID}
																	/>
																</>
															))}
												</div>
											</div>
										</>
									)}
								</>

								<div className="flex justify-end mt-4">
									{currentStep !== steps?.length - 1 && (
										<Button
											type="ghost"
											onClick={() => setCurrentStep(currentStep + 1)}
										>
											{`Optional: ${steps[currentStep + 1]?.name}`}
										</Button>
									)}

									<Button
										disabled={!hasAllRequired}
										onClick={() => onAddTask()}
									>
										Create Task
									</Button>
								</div>
							</div>
						</div>
					</div>
				)}
			</Modal>
		</>
	);
};

export default AddTaskModal;
